import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { getTransformOrigin, getTranslate3d } from "./transitionHelpers";
import { MotionState } from "./types";
export const PopperSpring = forwardRef(({ children, isOpen, motionState, placement, setMotionState, ...props }, ref) => {
    /**
     * In tests, return a simple mocked component to avoid async test complexities.
     * We test the full component features once in Popper.production.test.tsx
     */
    useEffect(() => {
        if (process.env.NODE_ENV === "test") {
            setMotionState(isOpen ? MotionState.Opened : MotionState.Closed);
        }
    }, [isOpen, setMotionState]);
    const transition = useMemo(() => ({
        default: { type: "spring", mass: 1, stiffness: 500, damping: 24 }
    }), []);
    const variants = useMemo(() => ({
        closed: {
            opacity: 0,
            scale: 0.9,
            transformOrigin: getTransformOrigin(placement),
            ...getTranslate3d(placement)
        },
        opened: {
            opacity: 1,
            scale: 1,
            x: 0,
            y: 0
        }
    }), [placement]);
    if (process.env.NODE_ENV === "test") {
        const { tabIndex, id, role, "aria-modal": ariaModal, "aria-labelledby": ariaLabelledBy } = props;
        return (_jsx("div", { ref: ref, tabIndex: tabIndex, id: id, role: role, "aria-modal": ariaModal, "aria-labelledby": ariaLabelledBy, "data-testid": "PopperSpring", "data-motion-state": motionState, children: children }));
    }
    /* end test mock */
    return (_jsx(motion.div, { ref: ref, ...props, initial: "closed", animate: isOpen ? "opened" : "closed", onAnimationStart: () => {
            setMotionState(isOpen ? MotionState.Opening : MotionState.Closing);
        }, onAnimationComplete: () => {
            setMotionState(isOpen ? MotionState.Opened : MotionState.Closed);
        }, transition: transition, variants: variants, "data-testid": "PopperSpring", "data-motion-state": motionState, children: children }));
});
